import type { MapRef } from 'react-map-gl';
import { forwardRef } from 'react';
import { Button, IMaybeAsync } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { ICommunityMapProps } from '~/components/molecules/CommunityMap';
import type { IContainerProps } from '~/components/utils/Container';
import { CommunityMap } from '~/components/molecules/CommunityMap';
import { IApiCrowdGeoSearchRequestBody } from '~/lib/api/modules/Crowd';
import { useGeoSearchCrowdQuery } from '~/lib/api/modules/Crowd/hooks/useGeoSearchCrowdQuery';

const COMMUNITY_MAP_GEOJSON_LIMIT = 5000;

export type ISearchCommunityMapProps = IContainerProps &
  ICommunityMapProps & {
    filter?: IApiCrowdGeoSearchRequestBody;
    onSearchWithin?: () => IMaybeAsync<void>;
  };

const styles = stylex.create({
  searchNearButton: {
    position: 'absolute',
    bottom: 10,
    left: '50%',
    transform: 'translateX(-50%)',
  },
});

export const SearchCommunityMap = forwardRef<MapRef, ISearchCommunityMapProps>(
  function SearchCommunityMap(props, forwardedRef) {
    const { appId, filter, onSearchWithin, ...other } = props;
    const { t } = useTranslation();
    const geoSearchCrowdQuery = useGeoSearchCrowdQuery(
      filter
        ? {
            appId,
            body: {
              ...filter,
              limit: COMMUNITY_MAP_GEOJSON_LIMIT,
              sortBy:
                typeof filter.lat === 'number' &&
                typeof filter.lng === 'number' &&
                (filter.radius ?? 0) > 0
                  ? 'distance'
                  : filter.sortBy,
              sortOrder:
                typeof filter.lat === 'number' &&
                typeof filter.lng === 'number' &&
                (filter.radius ?? 0) > 0
                  ? 'asc'
                  : filter.sortOrder,
            },
          }
        : undefined,
    );

    return (
      <CommunityMap
        appId={appId}
        data={geoSearchCrowdQuery.data}
        filter={filter}
        ref={forwardedRef}
        {...other}
      >
        <Button
          variant="elevated"
          sx={styles.searchNearButton}
          onClick={onSearchWithin}
          loading={geoSearchCrowdQuery.isFetching}
        >
          {t('SearchCommunityMap.searchNear')}
        </Button>
      </CommunityMap>
    );
  },
);
